/**
 * Ciclo de vida de sessão de replay (generation + status).
 */

import { getCoordinationEpoch } from '../coordination/coordinationEpoch';
import { getFlushLockFencingToken } from '../coordination/distributedLock';
import { getMinLeaderFencingToken, getTabId } from '../tabCoordinator';
import { isActiveOperationsInvalidated } from '../coordination/ownershipInvalidation';
import {
  recordReplayStarted,
  recordReplayCompleted,
  recordReplayFailed,
} from '../telemetry';

export type ReplaySessionStatus = 'idle' | 'running' | 'failed' | 'completed';

export interface ReplaySessionState {
  replayGenerationId: string;
  status: ReplaySessionStatus;
  startedAt: number;
  completedAt?: number;
  originalEventIds: string[];
  ownerEpoch: number;
  fencingToken: number;
  snapshotId?: string;
}

let active: ReplaySessionState | null = null;

export function beginReplaySession(
  eventIds: string[],
  opts?: { snapshotId?: string }
): ReplaySessionState {
  const epoch = getCoordinationEpoch();
  const fencingToken = Math.max(
    getFlushLockFencingToken(),
    getMinLeaderFencingToken()
  );
  active = {
    replayGenerationId: `rg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    status: 'running',
    startedAt: Date.now(),
    originalEventIds: [...eventIds],
    ownerEpoch: epoch,
    fencingToken,
    snapshotId: opts?.snapshotId,
  };
  recordReplayStarted();
  return active;
}

export function getActiveReplaySession(): ReplaySessionState | null {
  return active;
}

export function assertReplaySessionValid(): boolean {
  if (!active || active.status !== 'running') return true;
  if (isActiveOperationsInvalidated(getTabId())) {
    failReplaySession();
    return false;
  }
  const epoch = getCoordinationEpoch();
  const token = Math.max(
    getFlushLockFencingToken(),
    getMinLeaderFencingToken()
  );
  return active.ownerEpoch === epoch && active.fencingToken <= token;
}

export function completeReplaySession(): void {
  if (!active) return;
  active.status = 'completed';
  active.completedAt = Date.now();
  recordReplayCompleted();
  active = null;
}

export function failReplaySession(): void {
  if (!active) return;
  active.status = 'failed';
  active.completedAt = Date.now();
  recordReplayFailed();
}

export function _testResetReplaySession(): void {
  active = null;
}
